import React, { useEffect, useState } from 'react'

import { motion, usePresence } from 'framer-motion'
import { CheckIcon } from '@chakra-ui/icons'
import { Box, IconButton, Text, Tooltip } from '@chakra-ui/react'
import { Draggable } from '@hello-pangea/dnd'

import { SummaryPart } from '@pkg/types/interview'

import {
  useUpdateSummaryParts,
  useUpdateSummaryPartScore,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'
import { DraggableIcon } from '@/views/assets/DraggableIcon'
import { MagicStarsIcon } from '@/views/assets/MagicStarsIcon'
import { PencilSvg } from '@/views/assets/PencilSvg'
import { AutoResizeTextarea } from '@/views/components/AutoResizeTextarea/AutoResizeTextarea'
import Card from '@/views/components/Card'
import { CopyContentButton } from '@/views/components/CopyContentButton/CopyContentButton'
import { MarkdownRenderer } from '@/views/components/MarkdownRenderer/MarkdownRenderer'

import { EditSummaryPrompt } from './EditSummaryPrompt'
import { formatScorecardPartForCopy } from './formatScorecardPartTitle'
import { ScoreIndicator } from './ScoreIndicator'

type SummaryPartCardProps = {
  part: SummaryPart
  initialIndex: number
  isEditingParts: boolean[]
  setIsEditingParts: React.Dispatch<React.SetStateAction<boolean[]>>
  isAnimated: boolean
  idx: number
}

export const SummaryPartCard: React.FC<SummaryPartCardProps> = ({
  part,
  setIsEditingParts,
  isEditingParts,
  initialIndex,
  isAnimated,
  idx,
}) => {
  const [isPresent, safeToRemove] = usePresence()

  return isAnimated ? (
    <motion.div
      layout={true}
      onAnimationComplete={() => !isPresent && setTimeout(safeToRemove, 1000)}
    >
      <DraggablePart
        key={part.id}
        part={part}
        setIsEditingParts={setIsEditingParts}
        isEditingParts={isEditingParts}
        initialIndex={initialIndex}
        idx={idx}
      />
    </motion.div>
  ) : (
    <DraggablePart
      key={part.id}
      part={part}
      setIsEditingParts={setIsEditingParts}
      isEditingParts={isEditingParts}
      initialIndex={initialIndex}
      idx={idx}
    />
  )
}

type DraggablePartProps = Omit<SummaryPartCardProps, 'isAnimated'>
const DraggablePart: React.FC<DraggablePartProps> = ({
  initialIndex,
  idx,
  part,
  isEditingParts,
  setIsEditingParts,
}) => {
  const summaryPartRef = React.useRef<HTMLDivElement>(null)
  const [isPromptEditing, setIsPromptEditing] = useState<boolean>(false)

  const [textValue, setTextValue] = useState<string>(part.text)
  const updateSummaryParts = useUpdateSummaryParts()
  const updateSummaryPartScore = useUpdateSummaryPartScore()
  const isViewOnlyUser = useIsViewOnlyMember()

  const handleScoreChange = (newScore: number) => {
    updateSummaryPartScore.mutate({
      partId: part.id,
      score: newScore,
    })
  }

  useEffect(() => {
    setTextValue(part.text)
  }, [part.text])

  return (
    <Draggable draggableId={part.name} index={idx}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            marginBottom: '1.25rem',
            ...provided.draggableProps.style,
          }}
        >
          <Card className="flex flex-col flex-grow  pt-[1.25rem] pb-[1.75rem] group">
            <div ref={summaryPartRef}>
              {!isPromptEditing && (
                <div
                  className="flex flex-wrap items-center gap-4 mb-[0.75rem] min-h-10 relative px-5 sm:px-[2.5rem]"
                  {...provided.dragHandleProps}
                >
                  <DraggableIcon className="!hidden left-[20px] absolute top-1/2 -translate-y-1/2 sm:group-hover:!block" />
                  <Text
                    fontWeight="bold"
                    fontSize="1.25rem"
                    as={'div'}
                    lineHeight={1.15}
                    minW={0}
                    flex={'0 1 auto'}
                    overflowWrap="anywhere"
                  >
                    {part.name}
                  </Text>
                  <div className="flex items-center gap-4 flex-1 shrink-0">
                    {part.score !== null &&
                      part.score !== undefined &&
                      part.CustomSummaryPartPrompt?.hasScoring && (
                        <ScoreIndicator
                          score={part.score}
                          partId={part.id}
                          isEditable={!isViewOnlyUser}
                          onScoreChange={handleScoreChange}
                        />
                      )}
                    <div className="flex-1 min-w-0" />
                    <div className="gap-[0.6rem] flex shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      {!isEditingParts[initialIndex] && (
                        <CopyContentButton
                          textContent={{
                            // 'text/html': `<div style="font-family: serif"><strong>${localSummaryParts[initialIndex]?.name}</strong><br>${localSummaryParts[initialIndex]?.text}</div>`,
                            'text/plain': formatScorecardPartForCopy(part),
                          }}
                          contentRef={summaryPartRef}
                          variant="brand-solid-gray"
                          padding="0"
                          iconColor="black"
                        />
                      )}

                      {!isViewOnlyUser &&
                        part.customSummaryPartPromptId &&
                        !isEditingParts[initialIndex] && (
                          <Tooltip
                            rounded={'1.25rem'}
                            py={2}
                            px={4}
                            bgColor={'blackAlpha.900'}
                            label={'Edit prompt'}
                            shouldWrapChildren={true}
                          >
                            <IconButton
                              onClick={async () => {
                                setIsPromptEditing(!isPromptEditing)
                              }}
                              variant="brand-solid-gray"
                              icon={
                                isPromptEditing ? (
                                  <CheckIcon />
                                ) : (
                                  <MagicStarsIcon />
                                )
                              }
                              aria-label="Edit summary prompt"
                            />
                          </Tooltip>
                        )}

                      {!isViewOnlyUser && (
                        <IconButton
                          onClick={() => {
                            if (isEditingParts[initialIndex]) {
                              updateSummaryParts.mutate({
                                summaryParts: [
                                  {
                                    id: part.id,
                                    text: textValue,
                                  },
                                ],
                              })
                            }
                            setIsEditingParts([
                              ...isEditingParts.slice(0, initialIndex),
                              !isEditingParts[initialIndex],
                              ...isEditingParts.slice(initialIndex + 1),
                            ])
                          }}
                          variant="brand-solid-gray"
                          icon={
                            isEditingParts[initialIndex] ? (
                              <CheckIcon />
                            ) : (
                              <PencilSvg />
                            )
                          }
                          aria-label="Edit summary part"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="px-5 sm:px-[2.5rem]">
                <AutoResizeTextarea
                  rounded={'1.25rem'}
                  paddingX="1.25rem"
                  paddingY="0.65rem"
                  flexGrow={1}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    event.preventDefault()

                    setTextValue(event.target.value)
                  }}
                  value={textValue}
                  variant={'brand-solid-white'}
                  display={isEditingParts[initialIndex] ? 'block' : 'none'}
                />
                {isPromptEditing && part.customSummaryPartPromptId && (
                  <EditSummaryPrompt
                    partId={part.id}
                    id={part.customSummaryPartPromptId}
                    name={part.CustomSummaryPartPrompt?.name || ''}
                    text={part?.CustomSummaryPartPrompt?.text || ''}
                    hasScoring={
                      part.CustomSummaryPartPrompt?.hasScoring || false
                    }
                    setIsPromptEditing={setIsPromptEditing}
                  />
                )}
                <Box
                  flexGrow={1}
                  display={
                    isEditingParts[initialIndex] || isPromptEditing
                      ? 'none'
                      : 'block'
                  }
                >
                  <MarkdownRenderer text={textValue} className="flex-grow" />
                </Box>
              </div>
            </div>
          </Card>
        </div>
      )}
    </Draggable>
  )
}
